import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { CurrencySelect } from '@/components/currency-select'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage } from '@/lib/error-message'
import { orpc } from '@/lib/orpcClient'
import { useCurrencyOptions } from '@/lib/use-currency-options'
import { initialConvertCurrency } from './-convert-currency'
import { stageForPipeline } from './-convert-stage'

export const Route = createFileRoute('/crm/leads/$id/convert')({
  component: ConvertLeadPage,
})

type PreviewLead = {
  id: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  companyName?: string | null
  preferredCurrency?: string | null
}

type PreviewCandidates = {
  contact?: { id: string; firstName: string; lastName: string; email: string | null }
  account?: { id: string; name: string; website: string | null }
}

type Preview = {
  lead: PreviewLead
  candidates: PreviewCandidates
}

function ConvertLeadPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const { id } = Route.useParams()

  const { data, isLoading } = useQuery(
    orpc.crm.leads.previewConversion.queryOptions({ input: { id } }),
  )
  const preview = data as Preview | undefined

  // Pipelines are a small, tenant-configured set — this picker needs the whole
  // set in one request. 500 is LIST_MAX_LIMIT, the hard ceiling of any list
  // procedure (see apps/backend/src/lib/list-query.ts).
  const { data: pipelinesData } = useQuery(
    orpc.crm.pipelines.list.queryOptions({ input: { limit: 500 } }),
  )
  const pipelines =
    (pipelinesData?.rows as Array<{ id: string; name: string; isDefault: boolean }> | undefined) ??
    []
  const defaultPipelineId = useMemo(() => {
    return pipelines.find((p) => p.isDefault)?.id ?? pipelines[0]?.id ?? ''
  }, [pipelines])

  const [pipelineId, setPipelineId] = useState<string>('')
  useEffect(() => {
    if (!pipelineId && defaultPipelineId) setPipelineId(defaultPipelineId)
  }, [defaultPipelineId, pipelineId])

  const { data: stages } = useQuery(
    orpc.crm.stages.list.queryOptions({
      input: { pipelineId: pipelineId || '00000000-0000-0000-0000-000000000000' },
      enabled: !!pipelineId,
    }),
  )
  const stageIds = useMemo(
    () => ((stages as Array<{ id: string }> | undefined) ?? []).map((s) => s.id),
    [stages],
  )

  // Volgt de pipeline: een fase van een andere pipeline maakt plaats voor de eerste van deze.
  const [stageId, setStageId] = useState<string>('')
  useEffect(() => {
    const next = stageForPipeline(stageIds, stageId)
    if (next !== stageId) setStageId(next)
  }, [stageIds, stageId])

  const [contactChoice, setContactChoice] = useState<'create' | 'link'>('create')
  const [accountChoice, setAccountChoice] = useState<'create' | 'link'>('create')
  const [opportunityName, setOpportunityName] = useState<string>('')
  const [amount, setAmount] = useState<string>('0')
  const [currency, setCurrency] = useState<string>('')
  const { primaryCurrency } = useCurrencyOptions()

  // Seed derived defaults once the preview payload lands.
  useEffect(() => {
    if (!preview) return
    setOpportunityName((prev) => (prev !== '' ? prev : `${preview.lead.companyName ?? 'New'} deal`))
    setCurrency((prev) =>
      initialConvertCurrency(prev, preview.lead.preferredCurrency, primaryCurrency),
    )
    if (preview.candidates.contact) setContactChoice('link')
    if (preview.candidates.account) setAccountChoice('link')
  }, [preview, primaryCurrency])

  const convertMutation = useMutation(
    orpc.crm.leads.convert.mutationOptions({
      onSuccess: (res) => {
        toast.success(t('crm.lead.statusConverted'))
        const result = res as { opportunityId: string }
        navigate({
          to: '/crm/opportunities/$id',
          params: { id: result.opportunityId },
        })
      },
      onError: (e) => toast.error(errorMessage(e, t)),
    }),
  )

  if (isLoading || !preview) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const contactCandidate = preview.candidates.contact
  const accountCandidate = preview.candidates.account

  const handleSubmit = () => {
    if (!pipelineId || !stageId) {
      toast.error(`${t('crm.opportunity.pipeline')} / ${t('crm.opportunity.stage')}`)
      return
    }
    if (!opportunityName.trim()) {
      toast.error(t('crm.lead.opportunityName'))
      return
    }
    if (currency.length !== 3) {
      toast.error(t('crm.opportunity.currency'))
      return
    }
    convertMutation.mutate({
      id,
      pipelineId,
      stageId,
      opportunityName,
      opportunityAmount: amount || '0',
      opportunityCurrency: currency.toUpperCase(),
      createNewContact: contactChoice === 'create' ? true : undefined,
      linkExistingContactId:
        contactChoice === 'link' && contactCandidate ? contactCandidate.id : undefined,
      createNewAccount: accountChoice === 'create' ? true : undefined,
      linkExistingAccountId:
        accountChoice === 'link' && accountCandidate ? accountCandidate.id : undefined,
    })
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/crm/leads/$id', params: { id } })}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.cancel')}
        </Button>
        <h1 className="text-2xl font-bold">{t('crm.common.convert')}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('crm.contact.entity')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <RadioGroup
            value={contactChoice}
            onValueChange={(v) => setContactChoice(v as 'create' | 'link')}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="create" id="c-create" />
              <Label htmlFor="c-create">
                {t('crm.lead.createNewContact')} (
                {`${preview.lead.firstName ?? ''} ${preview.lead.lastName ?? ''}`.trim() ||
                  preview.lead.email ||
                  '—'}
                )
              </Label>
            </div>
            {contactCandidate && (
              <div className="flex items-center gap-2">
                <RadioGroupItem value="link" id="c-link" />
                <Label htmlFor="c-link">
                  {t('crm.lead.linkExistingContact')}: {contactCandidate.firstName}{' '}
                  {contactCandidate.lastName}{' '}
                  {contactCandidate.email ? `<${contactCandidate.email}>` : ''}
                </Label>
              </div>
            )}
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('crm.account.entity')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <RadioGroup
            value={accountChoice}
            onValueChange={(v) => setAccountChoice(v as 'create' | 'link')}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="create" id="a-create" />
              <Label htmlFor="a-create">
                {t('crm.lead.createNewAccount')} ({preview.lead.companyName ?? '—'})
              </Label>
            </div>
            {accountCandidate && (
              <div className="flex items-center gap-2">
                <RadioGroupItem value="link" id="a-link" />
                <Label htmlFor="a-link">
                  {t('crm.lead.linkExistingAccount')}: {accountCandidate.name}
                </Label>
              </div>
            )}
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('crm.opportunity.entity')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="opp-name">{t('crm.opportunity.name')}</Label>
            <Input
              id="opp-name"
              value={opportunityName}
              onChange={(e) => setOpportunityName(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="opp-pipeline">{t('crm.opportunity.pipeline')}</Label>
              <Select value={pipelineId} onValueChange={setPipelineId}>
                <SelectTrigger id="opp-pipeline">
                  <SelectValue placeholder={t('crm.opportunity.pipeline')} />
                </SelectTrigger>
                <SelectContent>
                  {pipelines.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="opp-stage">{t('crm.opportunity.stage')}</Label>
              <Select value={stageId} onValueChange={setStageId}>
                <SelectTrigger id="opp-stage">
                  <SelectValue placeholder={t('crm.opportunity.stage')} />
                </SelectTrigger>
                <SelectContent>
                  {((stages as Array<{ id: string; name: string }> | undefined) ?? []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="opp-amount">{t('crm.opportunity.amount')}</Label>
              <Input
                id="opp-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="opp-currency">{t('crm.opportunity.currency')}</Label>
              {/* Currency is seeded from the lead's preferred currency above, so
                  don't let the picker override it with the tenant primary. */}
              <CurrencySelect
                id="opp-currency"
                value={currency}
                onValueChange={setCurrency}
                defaultToPrimary={false}
                className="w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/crm/leads/$id', params: { id } })}
        >
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={convertMutation.isPending}>
          {convertMutation.isPending ? t('common.saving') : t('crm.lead.runConversion')}
        </Button>
      </div>
    </div>
  )
}
